import { Link } from "@tanstack/react-router";
import { marked } from "marked";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { askAiAssistant } from "~/server/ai-assistant";

type Source = { title: string; url: string; images?: string[] };
type Message = {
  role: "user" | "assistant";
  content: string;
  sources?: Source[];
};
const uiEnabled = import.meta.env.VITE_AI_ASSISTANT_ENABLED !== "false";
const CHAT_STORAGE_KEY = "hostright-ai-chat";
const OPEN_STORAGE_KEY = "hostright-ai-open";

function AssistantMark() {
  return (
    <span
      aria-hidden="true"
      className="grid size-10 place-items-center rounded-2xl bg-leaf text-xl text-ink"
    >
      ✦
    </span>
  );
}

function cleanAnswer(text: string) {
  const withoutThinkBlocks = text
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .trim();
  const finalMarker = withoutThinkBlocks.match(
    /(?:^|\n)\s*(?:final answer|final)\s*:\s*/i,
  );
  return finalMarker
    ? withoutThinkBlocks
        .slice(finalMarker.index! + finalMarker[0].length)
        .trim()
    : withoutThinkBlocks;
}

function isInternalReasoning(text: string) {
  return (
    /^\s*(thinking|analysis|reasoning)\b/i.test(text) ||
    /\*\*Analyze the Request:\*\*/i.test(text)
  );
}

function ThinkingIndicator() {
  return (
    <div className="ai-thinking">
      <span className="ai-thinking-mark">✦</span>
      <span>
        Thinking
        <span className="ai-thinking-dots" aria-hidden="true">
          …
        </span>
      </span>
    </div>
  );
}

function renderSources(sources: Source[] = []) {
  if (!sources.length) return null;
  return (
    <div className="ai-sources">
      <p className="eyebrow text-moss">From the Help Centre</p>
      <div className="mt-2 grid gap-1">
        {sources.slice(0, 3).map((source) => (
          <a
            key={source.url}
            href={source.url}
            className="text-sm font-bold text-moss hover:underline"
          >
            {source.title} ↗
          </a>
        ))}
      </div>
    </div>
  );
}

function renderAnswer(text: string, sources?: Source[]) {
  const answer = cleanAnswer(text);
  if (!answer || isInternalReasoning(answer)) return <ThinkingIndicator />;
  const allowedImages = new Set(
    sources?.flatMap((source) => source.images ?? []) ?? [],
  );
  const imageToken = /\[\[image:([^|\]]+)\|([^\]]+)\]\]/g;
  const segments: React.ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = imageToken.exec(answer))) {
    const markdown = answer.slice(cursor, match.index);
    if (markdown)
      segments.push(
        <div
          key={`text-${match.index}`}
          className="markdown-body ai-answer"
          dangerouslySetInnerHTML={{ __html: safeMarkdown(markdown, sources) }}
        />,
      );
    if (allowedImages.has(match[1]))
      segments.push(
        <figure key={`image-${match.index}`} className="ai-inline-image">
          <img src={match[1]} alt={match[2]} loading="lazy" />
          <figcaption>{match[2]}</figcaption>
        </figure>,
      );
    cursor = match.index + match[0].length;
  }
  const remainder = answer.slice(cursor);
  if (remainder)
    segments.push(
      <div
        key="text-end"
        className="markdown-body ai-answer"
        dangerouslySetInnerHTML={{ __html: safeMarkdown(remainder, sources) }}
      />,
    );
  return (
    <div className="flex gap-3">
      <span className="mt-1 grid size-7 shrink-0 place-items-center rounded-lg bg-leaf text-ink">
        ✦
      </span>
      <div className="min-w-0">
        {segments}
        {renderSources(sources)}
      </div>
    </div>
  );
}

function safeMarkdown(text: string, sources: Source[] = []) {
  const allowedLinks = new Set([
    "/knowledge-base",
    "https://dash.hostright.de",
    ...sources.map((source) => source.url),
  ]);
  const markdown = text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label: string, url: string) =>
      allowedLinks.has(url.trim()) ? match : label,
    )
    .replace(/\/knowledge-base\/[a-z0-9_./-]+/gi, (url) =>
      allowedLinks.has(url) ? url : "/knowledge-base",
    );
  let html = marked.parse(markdown, { breaks: true }) as string;
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+=("[^"]*"|'[^']*')/gi, "")
    .replace(/(href|src)=("|')\s*javascript:[^"']*("|')/gi, '$1="#"');
}

export function AiAssistant({
  floating = false,
  fullPage = false,
}: {
  floating?: boolean;
  fullPage?: boolean;
}) {
  const [open, setOpen] = useState(!floating);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [storageHydrated, setStorageHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const storedMessages = window.localStorage.getItem(CHAT_STORAGE_KEY);
      if (storedMessages) {
        const parsed = JSON.parse(storedMessages) as Message[];
        if (Array.isArray(parsed)) setMessages(parsed);
      }
      if (floating && window.localStorage.getItem(OPEN_STORAGE_KEY) === "true")
        setOpen(true);
    } catch {
      // Ignore unavailable or malformed local storage.
    }
    setStorageHydrated(true);
    const openAssistant = (event: Event) => {
      setOpen(true);
      const customEvent = event as CustomEvent<{ question?: string }>;
      if (customEvent.detail?.question)
        setQuestion(customEvent.detail.question);
    };
    window.addEventListener("hostright:open-ai", openAssistant);
    return () => window.removeEventListener("hostright:open-ai", openAssistant);
  }, [floating]);

  useEffect(() => {
    if (typeof window === "undefined" || !storageHydrated) return;
    try {
      window.localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
      if (floating) window.localStorage.setItem(OPEN_STORAGE_KEY, String(open));
    } catch {
      // Ignore unavailable local storage.
    }
  }, [floating, messages, open, storageHydrated]);
  if (!uiEnabled) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!question.trim() || loading) return;
    const next = [
      ...messages,
      { role: "user" as const, content: question.trim() },
    ];
    setMessages(next);
    setQuestion("");
    setError("");
    setLoading(true);
    try {
      const response = await askAiAssistant({
        data: { question: next.at(-1)!.content, history: messages },
      });
      setMessages([
        ...next,
        {
          role: "assistant",
          content: response.answer,
          sources: response.sources,
        },
      ]);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  function newConversation() {
    setMessages([]);
    setQuestion("");
    setError("");
  }

  if (floating && !open)
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ai-fab"
        aria-label="Ask the HostRight AI assistant"
      >
        <span>✦</span>
        <span>Ask AI</span>
      </button>
    );

  const shellClass = fullPage
    ? "flex h-[calc(100vh-4.5rem)] overflow-hidden bg-white"
    : floating
      ? "ai-popover"
      : "mx-auto max-w-5xl";
  return (
    <div className={shellClass}>
      {fullPage && (
        <aside className="hidden w-72 shrink-0 border-r border-ink/10 bg-cream p-6 lg:block">
          <div className="flex items-center gap-3">
            <AssistantMark />
            <div>
              <p className="text-sm font-bold">HostRight AI</p>
              <p className="text-xs text-ink/50">Pre-purchase assistant</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setMessages([]);
              setError("");
            }}
            className="mt-10 w-full rounded-xl border border-ink/15 px-4 py-3 text-left text-sm font-bold transition hover:border-moss hover:text-moss"
          >
            ＋ New conversation
          </button>
          <div className="mt-8 border-t border-ink/10 pt-6">
            <p className="eyebrow text-moss">Good to know</p>
            <p className="mt-3 text-sm leading-6 text-ink/60">
              This assistant uses HostRight&apos;s Help Centre to help you
              choose a service before purchase.
            </p>
            <Link
              to="/knowledge-base"
              className="mt-4 inline-block text-sm font-bold text-moss hover:underline"
            >
              Open Help Centre ↗
            </Link>
          </div>
        </aside>
      )}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
        <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4 md:px-10">
          <div>
            <p className="eyebrow text-moss">HostRight AI assistant</p>
            <h1 className="mt-1 text-lg font-bold tracking-tight">
              Ask before you buy.
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {floating && (
              <button
                type="button"
                onClick={newConversation}
                className="text-xs font-bold text-ink/55 hover:text-moss"
              >
                New chat
              </button>
            )}
            {floating && (
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-2xl text-ink/50 hover:text-ink"
                aria-label="Close assistant"
              >
                ×
              </button>
            )}
            {fullPage && (
              <Link
                to="/"
                className="text-sm font-bold text-ink/55 hover:text-moss"
              >
                Back to site
              </Link>
            )}
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            className={`mx-auto min-h-0 w-full max-w-4xl flex-1 space-y-6 overflow-y-auto px-5 py-8 md:px-10 ${fullPage ? "md:py-12" : "max-h-[32rem]"}`}
            aria-live="polite"
          >
            {messages.length === 0 && (
              <div
                className={`flex flex-col items-center justify-center text-center ${fullPage ? "min-h-[26rem]" : "py-4"}`}
              >
                <AssistantMark />
                <h2
                  className={`${fullPage ? "mt-6 text-4xl" : "mt-4 text-2xl"} display font-bold`}
                >
                  What are you building?
                </h2>
                <p className="mt-3 max-w-md text-sm leading-6 text-ink/55">
                  Ask about your stack, migration, domains, databases, or the
                  practical steps involved.
                </p>
                <div className="mt-6 grid w-full max-w-2xl gap-3 text-left md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() =>
                      setQuestion("Can I host a Node.js app with a database?")
                    }
                    className="rounded-2xl border border-ink/10 p-3 text-sm transition hover:border-moss hover:bg-cloud"
                  >
                    Can I host a Node.js app with a database?
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuestion("How do I connect my domain?")}
                    className="rounded-2xl border border-ink/10 p-3 text-sm transition hover:border-moss hover:bg-cloud"
                  >
                    How do I connect my domain?
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setQuestion("Is HostRight a good fit for WordPress?")
                    }
                    className="rounded-2xl border border-ink/10 p-3 text-sm transition hover:border-moss hover:bg-cloud"
                  >
                    Is HostRight a good fit for WordPress?
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setQuestion("How do I deploy an application?")
                    }
                    className="rounded-2xl border border-ink/10 p-3 text-sm transition hover:border-moss hover:bg-cloud"
                  >
                    How do I deploy an application?
                  </button>
                </div>
              </div>
            )}
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={
                  message.role === "user"
                    ? "ml-auto max-w-3xl rounded-3xl rounded-br-md bg-moss px-5 py-4 text-sm leading-7 text-cream"
                    : "max-w-3xl text-sm leading-7 text-ink/75"
                }
              >
                {message.role === "assistant"
                  ? renderAnswer(message.content, message.sources)
                  : message.content}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3 text-sm text-ink/55">
                <span className="grid size-7 place-items-center rounded-lg bg-leaf text-ink">
                  ✦
                </span>
                <span className="pt-1">Thinking through the Help Centre…</span>
              </div>
            )}
            {error && (
              <p className="rounded-xl border border-red-900/15 bg-red-50 p-3 text-sm text-red-900">
                {error}
              </p>
            )}
          </div>
          <div className="border-t border-ink/10 bg-white px-5 pb-5 pt-4 md:px-10">
            <form
              onSubmit={submit}
              className="mx-auto flex max-w-4xl gap-3 rounded-2xl border border-ink/15 bg-cream p-2 shadow-sm focus-within:border-moss"
            >
              <label
                className="sr-only"
                htmlFor={fullPage ? "ai-question-full" : "ai-question"}
              >
                Ask HostRight AI
              </label>
              <input
                id={fullPage ? "ai-question-full" : "ai-question"}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                maxLength={1200}
                placeholder="Message HostRight AI…"
                className="min-w-0 flex-1 bg-transparent px-3 text-sm outline-none"
              />
              <button
                type="submit"
                disabled={loading || !question.trim()}
                className="button-primary rounded-xl px-4 py-3 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-40"
              >
                Send
              </button>
            </form>
            <p className="mx-auto mt-3 max-w-4xl text-center text-xs text-ink/45">
              AI answers are for pre-purchase guidance, not support. Account
              help is handled in your dashboard.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
